import type { MockedResponse } from "@apollo/client/testing";
import { COMMISSION_CAMPAIGNS_QUERY } from "../campaigns/queries";
import { COMMISSION_PROGRAM_CONSENTS_QUERY } from "../consents/queries";
import { COMMISSION_ENROLLMENTS_QUERY } from "../enrollments/queries";
import { CREATE_COMMISSION_CAMPAIGN_MUTATION } from "../campaigns/mutations";
import { COMMISSION_PARTNERS_QUERY } from "../partners/queries";
import { CREATE_COMMISSION_PARTNER_MUTATION } from "../partners/mutations";
import { COMMISSION_PROGRAMS_QUERY } from "../programs/queries";
import { buildConnection } from "./connection";
import {
  commissionCampaignFixtures,
  commissionEnrollmentFixtures,
  commissionPartnerFixtures,
  commissionProgramFixtures
} from "./fixtures";
import { commissionProgramConsentFixtures } from "./personGrpFixtures";

function withTypenames<T>(node: T, typename: string) {
  return { __typename: typename, ...node };
}

export const commissionProgramsMockData = {
  commissionPrograms: buildConnection(
    "BackofficeCommissionProgramConnection",
    commissionProgramFixtures.map((node) => withTypenames(node, "BackofficeCommissionProgram"))
  )
};

export const commissionPartnersMockData = {
  commissionPartners: buildConnection(
    "BackofficeCommissionPartnerConnection",
    commissionPartnerFixtures.map((node) => withTypenames(node, "BackofficeCommissionPartner"))
  )
};

export const commissionCampaignsMockData = {
  commissionCampaigns: buildConnection(
    "BackofficeCommissionCampaignConnection",
    commissionCampaignFixtures.map((node) => ({
      __typename: "BackofficeCommissionCampaign",
      ...node,
      partner: withTypenames(node.partner, "BackofficeCommissionPartner"),
      program: withTypenames(node.program, "BackofficeCommissionProgramSummary")
    }))
  )
};

export const commissionEnrollmentsMockData = {
  commissionEnrollments: buildConnection(
    "BackofficeCommissionEnrollmentConnection",
    commissionEnrollmentFixtures.map((node) => ({
      __typename: "BackofficeCommissionEnrollment",
      ...node,
      person: node.person ? withTypenames(node.person, "BackofficeCommissionEnrollmentPerson") : null,
      program: withTypenames(node.program, "BackofficeCommissionProgramSummary")
    }))
  )
};

function buildConsentsMockData(campaignId: string, crmPersonId?: string) {
  const nodes = commissionProgramConsentFixtures
    .filter(
      (consent) => consent.commissionCampaignId === campaignId && (!crmPersonId || consent.crmPersonId === crmPersonId)
    )
    .map((node) => withTypenames(node, "BackofficeCommissionProgramConsent"));

  return {
    commissionProgramConsents: buildConnection("BackofficeCommissionProgramConsentConnection", nodes)
  };
}

function createCatalogMock(query: MockedResponse["request"]["query"], data: Record<string, unknown>): MockedResponse {
  return {
    request: { query },
    maxUsageCount: Infinity,
    variableMatcher: () => true,
    result: { data }
  };
}

export const commissionProgramsApolloMock = createCatalogMock(COMMISSION_PROGRAMS_QUERY, commissionProgramsMockData);
export const commissionPartnersApolloMock = createCatalogMock(COMMISSION_PARTNERS_QUERY, commissionPartnersMockData);
export const commissionCampaignsApolloMock = createCatalogMock(COMMISSION_CAMPAIGNS_QUERY, commissionCampaignsMockData);
export const commissionEnrollmentsApolloMock = createCatalogMock(
  COMMISSION_ENROLLMENTS_QUERY,
  commissionEnrollmentsMockData
);

export const commissionProgramConsentsApolloMock = createCatalogMock(
  COMMISSION_PROGRAM_CONSENTS_QUERY,
  buildConsentsMockData("commission-campaign-2026-acme", "person-ada")
);

export const createCommissionPartnerApolloMock: MockedResponse = {
  request: { query: CREATE_COMMISSION_PARTNER_MUTATION },
  variableMatcher: () => true,
  result: {
    data: {
      commissionCreatePartner: {
        partner: withTypenames(
          {
            id: "commission-partner-created",
            name: "Created Partner",
            legacyId: "SP-NEW",
            status: "ACTIVE"
          },
          "BackofficeCommissionPartner"
        )
      }
    }
  }
};

export const createCommissionCampaignApolloMock: MockedResponse = {
  request: { query: CREATE_COMMISSION_CAMPAIGN_MUTATION },
  variableMatcher: () => true,
  result: {
    data: {
      commissionCreateCampaign: {
        campaign: withTypenames(
          {
            id: "commission-campaign-created",
            editionYear: 2026,
            status: "DRAFT",
            payoutCadence: "QUARTERLY",
            startsAt: "2026-01-01T00:00:00Z",
            endsAt: "2026-12-31T23:59:59Z",
            partner: withTypenames(
              { id: "commission-partner-acme", name: "Acme Golf Sponsors" },
              "BackofficeCommissionPartner"
            ),
            program: withTypenames(
              { id: "commission-program-grp", name: "Golf Retirement Plus", slug: "grp" },
              "BackofficeCommissionProgramSummary"
            )
          },
          "BackofficeCommissionCampaign"
        )
      }
    }
  }
};

/** All commission catalog query mocks — use in Vitest, Storybook, or dev mock link. */
export const commissionCatalogApolloMocks: MockedResponse[] = [
  commissionProgramsApolloMock,
  commissionPartnersApolloMock,
  commissionCampaignsApolloMock,
  commissionEnrollmentsApolloMock,
  commissionProgramConsentsApolloMock,
  createCommissionPartnerApolloMock,
  createCommissionCampaignApolloMock
];

type MockResolver = (variables: Record<string, unknown>) => Record<string, unknown>;

export const commissionCatalogMockResults: Record<string, Record<string, unknown>> = {
  CommissionPrograms: commissionProgramsMockData,
  CommissionPartners: commissionPartnersMockData,
  CommissionCampaigns: commissionCampaignsMockData,
  CommissionEnrollments: commissionEnrollmentsMockData
};

export const commissionVariableMockResolvers: Record<string, MockResolver> = {
  CommissionProgramConsents: (variables) =>
    buildConsentsMockData(
      String(variables.campaignId),
      variables.crmPersonId ? String(variables.crmPersonId) : undefined
    ),
  CreateCommissionPartner: () => ({
    commissionCreatePartner: {
      partner: withTypenames(
        {
          id: "commission-partner-created",
          name: "Created Partner",
          legacyId: "SP-NEW",
          status: "ACTIVE"
        },
        "BackofficeCommissionPartner"
      )
    }
  }),
  CreateCommissionCampaign: () => ({
    commissionCreateCampaign: {
      campaign: withTypenames(
        {
          id: "commission-campaign-created",
          editionYear: 2026,
          status: "DRAFT",
          payoutCadence: "QUARTERLY",
          startsAt: "2026-01-01T00:00:00Z",
          endsAt: "2026-12-31T23:59:59Z",
          partner: withTypenames(
            { id: "commission-partner-acme", name: "Acme Golf Sponsors" },
            "BackofficeCommissionPartner"
          ),
          program: withTypenames(
            { id: "commission-program-grp", name: "Golf Retirement Plus", slug: "grp", status: "ACTIVE" },
            "BackofficeCommissionProgramSummary"
          )
        },
        "BackofficeCommissionCampaign"
      )
    }
  })
};
